import { z } from 'zod';
/**
 * The one list of themes (docs/partial/appearance-and-themes-spec.md 6.2). Web, API and the
 * theme check read it; nothing else enumerates themes. A theme is added by a token block in
 * `packages/ui/src/themes.css`, its name in the locale files and an entry here – `pnpm
 * theme:check` fails when one of the three is missing.
 *
 * `preview` is what the chooser paints before the theme is applied: the light background, the
 * card, the primary colour and two accents. The values repeat the token block on purpose – the
 * chooser shows every theme at once, the stylesheet only the active one.
 */
export const THEME_IDS = ['classic', 'confetti'];
export const themeSchema = z.enum(THEME_IDS);
export const THEMES = {
    classic: {
        id: 'classic',
        nameKey: 'appearance.themes.classic.name',
        preview: {
            background: 'oklch(99% 0.005 90)',
            card: 'oklch(100% 0 0)',
            primary: 'oklch(57% 0.17 45)',
            accents: ['oklch(94% 0.03 130)', 'oklch(93% 0.04 45)'],
        },
        pattern: true,
        picture: true,
    },
    confetti: {
        id: 'confetti',
        nameKey: 'appearance.themes.confetti.name',
        preview: {
            background: 'oklch(98.5% 0.014 75)',
            card: 'oklch(100% 0 0)',
            primary: 'oklch(56% 0.22 0)',
            accents: ['oklch(91% 0.07 195)', 'oklch(93% 0.11 98)'],
        },
        pattern: true,
        picture: true,
    },
};
/** Build-time default; an instance overrides it with DEFAULT_THEME. */
export const DEFAULT_THEME = 'classic';
export function isTheme(value) {
    return typeof value === 'string' && THEME_IDS.includes(value);
}
/** Any stored or received theme id → a theme that exists (a removed theme falls back). */
export function toTheme(value, fallback = DEFAULT_THEME) {
    return isTheme(value) ? value : fallback;
}
export const COLOR_MODES = ['SYSTEM', 'LIGHT', 'DARK'];
export const colorModeSchema = z.enum(COLOR_MODES);
export const BACKGROUND_STYLES = ['NONE', 'PATTERN', 'IMAGE', 'CUSTOM'];
export const backgroundStyleSchema = z.enum(BACKGROUND_STYLES);
/** How strongly an own photo is covered by the theme's background colour, in percent. */
export const BACKGROUND_DIM_MIN = 40;
export const BACKGROUND_DIM_MAX = 95;
export const BACKGROUND_DIM_DEFAULT = 80;
export const backgroundDimSchema = z.number().int().min(BACKGROUND_DIM_MIN).max(BACKGROUND_DIM_MAX);
/**
 * The scale of hues that carry a meaning (6.4): tokens `--color-cat-1 … -10`, each with a soft
 * surface and a readable foreground, in every theme and mode. Tags and collections store one.
 */
export const CATEGORY_HUE_COUNT = 10;
export const categoryHueSchema = z.number().int().min(1).max(CATEGORY_HUE_COUNT);
/** What a user chose; the same shape on the device and in the profile. */
export const appearanceSchema = z.object({
    theme: themeSchema,
    colorMode: colorModeSchema,
    backgroundStyle: backgroundStyleSchema,
    backgroundDim: backgroundDimSchema,
});
export const DEFAULT_APPEARANCE = {
    theme: DEFAULT_THEME,
    colorMode: 'SYSTEM',
    backgroundStyle: 'PATTERN',
    backgroundDim: BACKGROUND_DIM_DEFAULT,
};
//# sourceMappingURL=themes.js.map